import { useCallback, useContext, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';

import { AnalyticsContext } from 'contexts/analytics';
import { SessionContext } from 'contexts/session';
import { IFullCHartData } from 'contexts/analytics/types';
import { colors } from 'constants/styles/colors';
import { PngExportTeam, captureChartAsPng } from 'utils/chartPngExport';
import { formatChartXAxisLabel } from 'utils/chartXAxisLabel';
import { ExportChartView } from 'utils/analyticsPageExport/chartSpecs';
import { ExportChartContext, ExportChartItem } from 'utils/analyticsPageExport/chartData';
import {
  CaptureProgress,
  ExportChartCaptureResult,
  runChartCapture,
} from 'utils/analyticsPageExport/captureOrchestrator';
import ActiveUsersChart from '../ActiveUsersChart';
import { CaptureHost } from './styles';

interface RenderState {
  view: ExportChartView;
  chartItems: ExportChartItem[];
  dataDisplay: { key: string; checked: boolean }[];
}

// Gives recharts a couple of frames to measure the off-viewport host and lay out
// the SVG before it is captured.
const defaultWaitForLayout = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 0)));
  });

interface UseExportChartCaptureReturn {
  hostElement: JSX.Element;
  captureAll: (opts?: {
    waitForLayout?: () => Promise<void>;
  }) => Promise<ExportChartCaptureResult[]>;
  isCapturing: boolean;
  progress: CaptureProgress | null;
}

export function useExportChartCapture(): UseExportChartCaptureReturn {
  const { filters } = useContext(AnalyticsContext);
  const { user, account, isAccountOwner, hasSkillDive } = useContext(SessionContext);

  const hostRef = useRef<HTMLDivElement>(null);
  const [renderState, setRenderState] = useState<RenderState | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [progress, setProgress] = useState<CaptureProgress | null>(null);

  const buildContext = useCallback((): ExportChartContext => {
    const managedTeamIds = (user?.teams ?? [])
      .filter((team) => team.is_manager)
      .map((team) => team.team_id)
      .join(',');
    return {
      date_from: filters?.date_from,
      date_to: filters?.date_to,
      period: filters?.period,
      team_ids: filters?.team_ids || (isAccountOwner ? '' : managedTeamIds),
      user_id: user?.uaa_id,
      account_id: account?.id,
    };
  }, [filters, user, account, isAccountOwner]);

  const captureAll = useCallback(
    async (opts?: { waitForLayout?: () => Promise<void> }): Promise<ExportChartCaptureResult[]> => {
      const waitForLayout = opts?.waitForLayout ?? defaultWaitForLayout;

      const ctx = buildContext();
      if (!ctx.user_id || !ctx.account_id) {
        throw new Error('export_capture_missing_session');
      }

      const dateRange =
        filters?.date_from && filters?.date_to
          ? `${format(parseISO(filters.date_from), 'MMM d, yyyy')} – ${format(
              parseISO(filters.date_to),
              'MMM d, yyyy'
            )}`
          : undefined;
      const teamFilter: PngExportTeam[] =
        filters?.team_display && filters.team_display.length > 0
          ? filters.team_display.map(({ name, color }) => ({
              name,
              bgColor: color ? colors.dataVisualization[color]?.lighter : undefined,
            }))
          : [{ name: isAccountOwner ? 'All users from the account' : 'All my teams' }];

      setIsCapturing(true);
      try {
        return await runChartCapture({
          ctx,
          hasSkillDive: !!hasSkillDive,
          onProgress: setProgress,
          captureOne: async ({ spec, chartItems, dataDisplay, series }) => {
            setRenderState({ view: spec.view, chartItems, dataDisplay });
            await waitForLayout();
            const host = hostRef.current;
            if (!host) return { ok: false, reason: 'render_failed' };
            return captureChartAsPng(host, { title: spec.title, dateRange, series, teamFilter });
          },
        });
      } finally {
        setRenderState(null);
        setProgress(null);
        setIsCapturing(false);
      }
    },
    [buildContext, filters, isAccountOwner, hasSkillDive]
  );

  const hostElement = (
    <CaptureHost aria-hidden="true">
      <div
        ref={hostRef}
        data-testid="export-chart-capture-host"
        style={{ width: '100%', height: '100%' }}
      >
        {renderState && (
          <ActiveUsersChart
            key={renderState.view}
            items={renderState.chartItems as unknown as IFullCHartData[]}
            chartType="AreaChart"
            chartDataType={renderState.view}
            Xaxis="date"
            Yaxis="count"
            dataDisplay={renderState.dataDisplay}
            formatXAxisLabel={(date, index) => formatChartXAxisLabel(date, index, filters?.period)}
          />
        )}
      </div>
    </CaptureHost>
  );

  return { hostElement, captureAll, isCapturing, progress };
}
